import { useState } from "react";
import { Link } from "react-router-dom";
import { Table, Button, Modal, Form, Input, DatePicker, message, Space, Popconfirm } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useSprints,
  useSprintTasks,
  useCreateSprint,
  useUpdateSprint,
  useDeleteSprint,
} from "../../queries/sprints";
import { useUpdateTask } from "../../queries/tasks";
import { useProjects } from "../../queries/projects";
import { useStaffDirectory, StaffMember } from "../../queries/users";
import { SprintProps, SprintStatus, TaskProps, ProjectProps } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { SprintStatusTag, TaskStatusTag, SeverityChip, PriorityChip } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import { routes } from "../../navigation/routes/routes";

/** Tasks of one sprint, across projects, with a way to send one back to the backlog. */
const SprintTasks = ({ sprintId }: { sprintId: string }) => {
  const { data: tasks, isLoading } = useSprintTasks(sprintId);
  const { data: projects } = useProjects();
  const { data: staff } = useStaffDirectory();
  const updateTask = useUpdateTask();
  const projectName = (id: string) => (projects || []).find((p: ProjectProps) => p._id === id)?.name ?? "—";
  const staffName = (id: string | null) => (id ? (staff || []).find((u: StaffMember) => u._id === id)?.name ?? "—" : "Unassigned");

  return (
    <Table<TaskProps>
      rowKey="_id"
      size="small"
      loading={isLoading}
      dataSource={tasks || []}
      pagination={false}
      locale={{
        emptyText: (
          <EmptyState
            title="No tasks in this sprint yet"
            description="Add tasks from the backlog by choosing this sprint in the Sprint column."
            actions={<Link to={routes.backlog}>Open the backlog</Link>}
          />
        ),
      }}
      columns={[
        {
          title: "Task", dataIndex: "title",
          render: (title: string, t) => <Link to={`/tasks/${t._id}`} style={{ fontWeight: 500 }}>{title}</Link>,
        },
        { title: "Project", dataIndex: "projectId", width: 170, render: (id: string) => projectName(id) },
        { title: "Severity", dataIndex: "severity", width: 120, render: (v) => <SeverityChip severity={v} /> },
        { title: "Priority", dataIndex: "priority", width: 110, render: (v) => <PriorityChip priority={v} /> },
        { title: "Assignee", dataIndex: "assigneeId", width: 150, render: (id: string | null) => staffName(id) },
        { title: "Status", dataIndex: "status", width: 130, render: (v) => <TaskStatusTag status={v} /> },
        {
          title: "", width: 150, align: "right",
          render: (_, t) => (
            <Button
              size="small"
              onClick={() =>
                updateTask.mutate(
                  { id: t._id, data: { sprintId: null } },
                  { onError: (err) => message.error(err instanceof ApiError ? err.message : "Could not update the task") }
                )
              }
            >
              Remove from sprint
            </Button>
          ),
        },
      ]}
    />
  );
};

/** Sprints are global: one sprint can hold tasks from several projects. */
export const SprintListPage = () => {
  const { data: sprints, isLoading, isError, error } = useSprints();
  const createSprint = useCreateSprint();
  const updateSprint = useUpdateSprint();
  const deleteSprint = useDeleteSprint();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleCreate = async () => {
    const values = await form.validateFields();
    try {
      await createSprint.mutateAsync({
        name: values.name,
        goal: values.goal,
        startDate: values.range[0].toISOString(),
        endDate: values.range[1].toISOString(),
      });
      setIsModalOpen(false);
      form.resetFields();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not create the sprint");
    }
  };

  const active = (sprints || []).find((s: SprintProps) => s.status === "active");

  const changeStatus = (sprint: SprintProps, status: SprintStatus) => {
    updateSprint.mutate(
      { id: sprint._id, data: { status } },
      { onError: (err) => message.error(err instanceof ApiError ? err.message : "Could not update the sprint") }
    );
  };

  return (
    <PageShell
      kind="list"
      kicker="All projects"
      title="Sprints"
      subtitle="A sprint can hold tasks and tickets from any project. Add them from the backlog."
      meta={[
        { label: "Current sprint", value: active ? active.name : <span style={{ color: "var(--osi-text-faint)" }}>None active</span> },
        {
          label: "Ends",
          value: active
            ? <span className="osi-num">{dayjs(active.endDate).format("D MMM")}</span>
            : <span style={{ color: "var(--osi-text-faint)" }}>—</span>,
        },
      ]}
      actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>New sprint</Button>}
    >
      <Toolbar count={(sprints || []).length} unit="sprints" />
      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<SprintProps>
          rowKey="_id"
          size="small"
          dataSource={sprints || []}
          pagination={false}
          expandable={{ expandedRowRender: (sprint) => <SprintTasks sprintId={sprint._id} /> }}
          locale={{
            emptyText: (
              <EmptyState
                title="No sprints planned"
                description="Open a sprint to give the board a cadence and start collecting velocity."
                actions={<Button type="primary" onClick={() => setIsModalOpen(true)}>New sprint</Button>}
              />
            ),
          }}
          columns={[
            { title: "Name", dataIndex: "name", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
            {
              title: "Goal", dataIndex: "goal",
              render: (v?: string) => v
                ? <span style={{ color: "var(--osi-text-muted)" }}>{v}</span>
                : <span style={{ color: "var(--osi-text-faint)" }}>—</span>,
            },
            { title: "Status", dataIndex: "status", width: 140, render: (s: SprintStatus) => <SprintStatusTag status={s} /> },
            {
              title: "Dates", width: 190, align: "right",
              render: (_, sprint) => (
                <span className="osi-num" style={{ color: "var(--osi-text-muted)" }}>
                  {dayjs(sprint.startDate).format("D MMM")} – {dayjs(sprint.endDate).format("D MMM YYYY")}
                </span>
              ),
            },
            {
              title: "", width: 230, align: "right",
              render: (_, sprint) => (
                <Space size="small">
                  {sprint.status === "planned" && (
                    <Button size="small" aria-label={`Start ${sprint.name}`} onClick={() => changeStatus(sprint, "active")}>Start</Button>
                  )}
                  {sprint.status === "active" && (
                    <Button size="small" type="primary" aria-label={`Complete ${sprint.name}`} onClick={() => changeStatus(sprint, "completed")}>Complete</Button>
                  )}
                  <Popconfirm
                    title="Delete this sprint?"
                    description="Its tasks return to the backlog."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => deleteSprint.mutate(sprint._id, {
                      onError: (err) => message.error(err instanceof ApiError ? err.message : "Could not delete the sprint"),
                    })}
                  >
                    <Button danger size="small" aria-label={`Delete ${sprint.name}`}>Delete</Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </DisplayComponent>

      <Modal
        title="New sprint"
        open={isModalOpen}
        onOk={handleCreate}
        okText="Create sprint"
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={createSprint.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="goal" label="Goal"><Input.TextArea rows={2} placeholder="One sentence the team can hold in their head" /></Form.Item>
          <Form.Item name="range" label="Dates" rules={[{ required: true }]}>
            <DatePicker.RangePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
